import React, { Suspense, useCallback, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Badge from 'react-bootstrap/Badge';
import Select from 'react-select';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { useTax1099Api } from '../../resources/hooks/api/useTax1099ApiHook';
import { useAllVendors } from '../../resources/useAllVendors';
import { URLS } from '../../resources/constants';
import { getBranch } from '../../store/user/selectors';
import VendorFormModal, { EMPTY_VENDOR, VendorFormValues } from '../Tax1099Vendors/VendorFormModal';
import type { CheckBox } from './CheckStatementViewer';

// The page viewer pulls in the PDF library, so it is loaded only when a check is actually opened.
const CheckStatementViewer = React.lazy(() => import('./CheckStatementViewer'));

/** A box is only used when it is four numbers inside the page; anything else opens the page as it always has. */
const validBox = (value: any): value is CheckBox => Array.isArray(value) && value.length === 4
  && value.every((n) => typeof n === 'number' && n >= 0 && n <= 1000) && value[2] > value[0] && value[3] > value[1];

// Up to this many imported files are shown as buttons; more than that become a list.
const FILE_BUTTON_LIMIT = 4;

/**
 * Assign a vendor to the bank payments that were read off a check.
 *
 * Extraction proposes the payee; nothing is written to a 1099 until someone
 * confirms here. Confirmation covers exactly the transaction IDs this screen
 * displayed -- if the server has since seen others, it rejects the submission
 * as stale rather than quietly including them.
 */

interface PayeeTransaction {
  transaction_id: number;
  amount: number;
  posting_date?: string | null;
  description?: string | null;
  extracted_check_data?: any;
}

interface PayeeCluster {
  payee_key: string;
  vendor_name: string;
  transaction_count: number;
  total_amount: number;
  earliest_date?: string | null;
  latest_date?: string | null;
  suggested_vendors: Array<{ id: number; display_name: string; vendor_code?: string }>;
  transactions: PayeeTransaction[];
}

const ADDRESS_FIELDS = [
  'payee_address_line1', 'payee_address_line2', 'payee_city', 'payee_state', 'payee_postal_code',
] as const;

const addressOf = (check: any) => {
  if (!check) return null;
  const parts = ADDRESS_FIELDS.map((f) => String(check[f] ?? '').trim());
  return parts.some(Boolean) ? parts : null;
};

/**
 * The addresses a cluster's checks carry, newest first, de-duplicated.
 *
 * A payee can be paid by several checks, and they need not agree. Taking
 * whichever happened to be first in the list would pre-fill an arbitrary one
 * and hide the disagreement, so the newest is proposed (transaction id breaks a
 * date tie) and any others are offered for the reviewer to pick instead.
 * Partial addresses are never merged: each is whole or not used.
 */
const checkAddresses = (cluster: PayeeCluster) => {
  const withAddress = cluster.transactions
    .filter((t) => addressOf(t.extracted_check_data))
    .sort((a, b) => {
      const byDate = String(b.posting_date || '').localeCompare(String(a.posting_date || ''));
      return byDate !== 0 ? byDate : b.transaction_id - a.transaction_id;
    });

  const seen = new Set<string>();
  return withAddress.filter((t) => {
    const key = (addressOf(t.extracted_check_data) || []).join('|');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// A raised card: tinted from the accent, an accent edge on top, and layered shadows so it lifts off the page.
const summaryCardStyle = (accent: string): React.CSSProperties => ({
  background: `linear-gradient(180deg, #ffffff 0%, ${accent}22 100%)`,
  border: '1px solid #dee2e6',
  borderTop: `4px solid ${accent}`,
  borderRadius: 10,
  boxShadow: '0 1px 1px rgba(0,0,0,.06), 0 4px 8px rgba(0,0,0,.10), 0 10px 20px rgba(0,0,0,.08), inset 0 1px 0 rgba(255,255,255,.9)',
});

const money = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

const Tax1099PayeeReview: React.FC = () => {
  const { t } = useTranslation('Tax1099PayeeReview');
  const branchData: any = useSelector(getBranch);
  const branchId = Number(branchData?.id);

  const {
    fetchUnmappedPayees, resolvePayee, createVendor, fetchNextVendorCode,
    openStatementEvidence,
  } = useTax1099Api();

  const [loading, setLoading] = useState(false);
  const [clusters, setClusters] = useState<PayeeCluster[]>([]);
  const { vendors: allVendors } = useAllVendors({ branchId });
  const [vendors, setVendors] = useState<Array<{ value: number; label: string }>>([]);
  const [chosen, setChosen] = useState<Record<string, number | null>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [creatingFor, setCreatingFor] = useState<PayeeCluster | null>(null);
  const [fileKey, setFileKey] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [viewer, setViewer] = useState<{ file: Blob; page: number; box: CheckBox; checkNumber?: string | null } | null>(null);
  const [formInitial, setFormInitial] = useState<VendorFormValues>(EMPTY_VENDOR);
  const [creating, setCreating] = useState(false);
  // Set once a payee is resolved here, so the way on to deposit classification
  // is offered only to someone who came to finish a month close, not on every visit.
  const [resolvedAny, setResolvedAny] = useState(false);

  const load = useCallback(() => {
    if (!branchId) return;
    setLoading(true);
    fetchUnmappedPayees({ branch_id: branchId }, (_m: string, resp: any) => {
      setClusters(resp?.data?.data || []);
      setLoading(false);
    }, (message: string) => {
      setLoading(false);
      toast.error(message || t('errors.load_failed'));
    });
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  // Local state is kept so a freshly created vendor can be prepended and stay
  // selectable; it is seeded from the shared list rather than from a single
  // 200-row page, which used to hide every vendor past the 200th.
  useEffect(() => {
    setVendors(
      allVendors.map((v: any) => ({
        value: v.id,
        label: v.vendor_code ? `${v.display_name} (${v.vendor_code})` : v.display_name,
      })),
    );
  }, [allVendors]);

  /**
   * Open the bank statement the check was read from, at its page.
   *
   * The tab is opened synchronously on the click so the popup blocker lets it
   * through, then pointed at the blob once gl-api answers.
   */
  const openEvidence = (evidence: any, key: string) => {
    // A check whose position on its page is known is shown in the app, lit up on the page. Without one the
    // statement opens in a tab at its page, as before.
    if (validBox(evidence.source_box) && evidence.source_page) {
      setOpening(key);
      openStatementEvidence(
        { file_id: evidence.source_file_id, branch_id: branchId },
        (_m: string, blob: Blob) => {
          setOpening(null);
          setViewer({ file: blob, page: evidence.source_page, box: evidence.source_box, checkNumber: evidence.check_number });
        },
        (message: string) => {
          setOpening(null);
          toast.error(message || t('errors.source_unavailable'));
        },
      );
      return;
    }
    const tab = window.open('', '_blank');
    setOpening(key);
    openStatementEvidence(
      { file_id: evidence.source_file_id, branch_id: branchId },
      (_m: string, blob: Blob) => {
        setOpening(null);
        const url = window.URL.createObjectURL(blob);
        const page = evidence.source_page ? `#page=${evidence.source_page}` : '';
        if (tab) { tab.location.href = url + page; } else { window.open(url + page, '_blank'); }
      },
      (message: string) => {
        setOpening(null);
        tab && tab.close();
        toast.error(message || t('errors.source_unavailable'));
      },
    );
  };

  // The in-app page could not be drawn, or the reader asked for the whole statement.
  const openInTab = () => {
    if (!viewer) return;
    window.open(`${window.URL.createObjectURL(viewer.file)}#page=${viewer.page}`, '_blank');
    setViewer(null);
  };

  const assign = (cluster: PayeeCluster, vendorId: number) => {
    setSaving(cluster.payee_key);
    resolvePayee({
      branch_id: branchId,
      payee_key: cluster.payee_key,
      target_vendor_id: vendorId,
      // Exactly the rows shown, so confirmation cannot cover more than was read.
      transaction_ids: cluster.transactions.map((t) => t.transaction_id),
    }, (_m: string, resp: any) => {
      const data = resp?.data?.data || {};
      setSaving(null);
      // Both counts, because creating a vendor triggers a sync that can assign
      // an exact payee match before this confirmation arrives. Reporting only
      // the rows this request wrote would say "0 transaction(s)" for a cluster
      // it had in fact just resolved.
      const resolved = Number(data.updated_transactions || 0)
        + Number(data.already_assigned_transactions || 0);
      toast.success(t('toasts.assigned', {
        from: cluster.vendor_name,
        to: data.target_vendor_name,
        transactions: t('counts.transactions', { count: resolved }),
        payments: t('counts.payments', { count: Number(data.inserted_payments || 0) }),
      }));
      setResolvedAny(true);
      load();
    }, (message: string, resp: any) => {
      setSaving(null);
      const failure = resp?.data?.message || message || '';
      if (`${failure}`.includes('stale')) {
        toast.error(t('errors.stale'));
        load();
        return;
      }
      toast.error(failure || t('errors.assign_failed'));
    });
  };

  const confirm = (cluster: PayeeCluster) => {
    const vendorId = chosen[cluster.payee_key];
    if (!vendorId) {
      toast.error(t('errors.choose_vendor'));
      return;
    }
    assign(cluster, vendorId);
  };

  /**
   * Open the vendor form pre-filled from the check. The payee name and any
   * address the extractor was willing to stand behind come across; everything
   * else is left for the person to fill in, because a 1099 vendor carries more
   * than a check prints.
   */
  const startCreateFromCheck = (cluster: PayeeCluster, fromTransactionId?: number) => {
    const named = cluster.transactions.find((t) => t.extracted_check_data)?.extracted_check_data || {};
    const candidates = checkAddresses(cluster);
    const chosenSource = fromTransactionId
      ? candidates.find((t) => t.transaction_id === fromTransactionId)
      : candidates[0];
    const check = chosenSource ? chosenSource.extracted_check_data : named;
    setFormInitial({
      ...EMPTY_VENDOR,
      vendor_code: '',
      source: 'manual',
      display_name: named.payee_name || cluster.vendor_name || '',
      address_line1: check.payee_address_line1 || '',
      address_line2: check.payee_address_line2 || '',
      city: check.payee_city || '',
      state: check.payee_state || '',
      postal_code: check.payee_postal_code || '',
    });
    setCreatingFor(cluster);
    fetchNextVendorCode({ branch_id: branchId }, (_m: string, resp: any) => {
      const next = resp?.data?.data?.vendor_code;
      if (next) setFormInitial((current) => ({ ...current, vendor_code: next }));
    });
  };

  const createThenAssign = (values: VendorFormValues) => {
    const cluster = creatingFor;
    if (!cluster) return;

    setCreating(true);
    const payload: Record<string, any> = {
      branch_id: branchId,
      display_name: values.display_name.trim(),
      status: Number(values.status),
      recipient_edelivery_consent: values.recipient_edelivery_consent,
      vendor_code: values.vendor_code.trim(),
    };
    ['contact_name', 'email', 'phone', 'address_line1', 'address_line2', 'city', 'state',
      'postal_code', 'default_form_type', 'default_box'].forEach((field) => {
      const value = String((values as any)[field] ?? '').trim();
      if (value) payload[field] = value;
    });
    if (values.vendor_type !== '') payload.vendor_type = Number(values.vendor_type);
    if (values.default_account_chart_id) payload.default_account_chart_id = values.default_account_chart_id;
    if (values.is_1099_eligible !== '') payload.is_1099_eligible = Number(values.is_1099_eligible);
    if (values.vendor_type === '2') {
      payload.is_1099_eligible = 0;
      payload.default_form_type = null;
      payload.default_box = null;
    }

    createVendor(payload, (_m: string, resp: any) => {
      setCreating(false);
      setCreatingFor(null);
      const created = resp?.data?.data;
      const newId = created?.id;
      if (!newId) {
        toast.error(t('errors.created_without_id'));
        load();
        return;
      }
      // Keep the new vendor selectable: if assignment fails the person retries
      // against it rather than creating a second one.
      setVendors((list) => [
        { value: newId, label: created.vendor_code ? `${created.display_name} (${created.vendor_code})` : created.display_name },
        ...list,
      ]);
      setChosen((c) => ({ ...c, [cluster.payee_key]: newId }));
      assign(cluster, newId);
    }, (message: string) => {
      setCreating(false);
      toast.error(message || t('errors.create_failed'));
    });
  };

  // Only payments whose payee is actually known belong here. The endpoint
  // returns every expense without a vendor, which on a real branch is mostly
  // Venmo, card purchases and transfers between the customer's own accounts --
  // none of which is a 1099 vendor payment. A check is what names a payee.
  const hasCheck = (c: PayeeCluster) => c.transactions.some((t) => t.extracted_check_data);

  // Each check belongs to the statement file it was read from, so the review can be narrowed to one import.
  const fileOf = (tx: PayeeTransaction) => {
    const data = tx.extracted_check_data;
    return data ? String(data.source_pdf_to_csv_id ?? data.source_file_id ?? 'unknown') : null;
  };
  const files: Array<{ key: string; name: string; checks: number }> = [];
  clusters.forEach((c) => c.transactions.forEach((tx) => {
    const key = fileOf(tx);
    if (!key) return;
    const found = files.find((f) => f.key === key);
    if (found) found.checks += 1;
    else files.push({ key, name: tx.extracted_check_data?.source_file_name || t('files.unknown'), checks: 1 });
  }));
  const totalChecks = files.reduce((n, f) => n + f.checks, 0);
  const activeFile = files.some((f) => f.key === fileKey) ? fileKey : null;
  // Narrowing to a file keeps only that file's rows, so a payee's count and total are for that file alone.
  const scoped = activeFile
    ? clusters.map((c) => {
      const rows = c.transactions.filter((tx) => fileOf(tx) === activeFile);
      return { ...c, transactions: rows, transaction_count: rows.length, total_amount: rows.reduce((sum, tx) => sum + Number(tx.amount || 0), 0) };
    }).filter((c) => c.transactions.length)
    : clusters;
  const visible = scoped.filter(hasCheck);

  // Summary of what is awaiting review (the same clusters the list below shows).
  const checks = visible.reduce((n, c) => n + c.transactions.filter((tx) => tx.extracted_check_data).length, 0);
  const withSuggestion = visible.filter((c) => c.suggested_vendors?.length).length;
  const summary = [
    { key: 'payees', accent: '#0d6efd', value: String(visible.length) },
    { key: 'checks', accent: '#0aa2c0', value: String(checks) },
    { key: 'transactions', accent: '#6f42c1', value: String(visible.reduce((n, c) => n + c.transaction_count, 0)) },
    { key: 'total_amount', accent: '#198754', value: money(visible.reduce((sum, c) => sum + c.total_amount, 0)) },
    { key: 'with_suggestion', accent: '#d97706', value: String(withSuggestion) },
  ];

  if (loading) {
    return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;
  }

  return (
    <div className="main-container flex-grow-1">
      <div className="container-fluid py-3">
        {viewer && (
          <Suspense fallback={null}>
            <CheckStatementViewer
              file={viewer.file}
              page={viewer.page}
              box={viewer.box}
              checkNumber={viewer.checkNumber}
              onClose={() => setViewer(null)}
              onFallback={openInTab}
            />
          </Suspense>
        )}
        <h5 className="fw-bold mb-1">{t('title')}</h5>
        <small className="text-muted d-block mb-3">
          {t('subtitle')}
        </small>

        {files.length > 1 && (
          <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 8 }} data-testid="file-filter">
            <span className="text-muted" style={{ fontSize: 14 }}>{t('files.label')}</span>
            {files.length > FILE_BUTTON_LIMIT ? (
              // Many files would run off the page as buttons, so they go in a list.
              <select
                className="form-control"
                style={{ width: 'auto', maxWidth: 420, fontSize: 14 }}
                aria-label={t('files.label')}
                value={activeFile ?? ''}
                onChange={(e) => setFileKey(e.target.value || null)}
              >
                <option value="">{`${t('files.all')} · ${t('files.checks', { count: totalChecks })}`}</option>
                {files.map((f) => <option key={f.key} value={f.key}>{`${f.name} · ${t('files.checks', { count: f.checks })}`}</option>)}
              </select>
            ) : (
              [{ key: null as string | null, name: t('files.all'), checks: totalChecks }, ...files].map((f) => (
                <Button
                  key={f.key ?? 'all'}
                  variant={activeFile === f.key ? 'primary' : 'outline-primary'}
                  title={f.name}
                  style={{ fontSize: 14, fontWeight: 500, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  onClick={() => setFileKey(f.key)}
                >
                  {`${f.name} · ${t('files.checks', { count: f.checks })}`}
                </Button>
              ))
            )}
          </div>
        )}

        {visible.length > 0 && (
          <div className="row g-2 mb-3" data-testid="payee-summary">
            {summary.map(({ key, value, accent }) => (
              <div key={key} className="col-6 col-md-4 col-xl">
                <div className="card h-100" style={summaryCardStyle(accent)}>
                  <div className="card-body py-2 px-3">
                    <div className="fs-4 fw-bold lh-sm" style={{ color: accent }} data-testid={`summary-${key}`}>{value}</div>
                    <div className="small text-muted">{t(`summary.${key}`)}</div>
                  </div>
                </div>
              </div>
            ))}
            <div className="col-12 small text-muted">{t('summary.scope')}</div>
          </div>
        )}

        {resolvedAny && (
          <div className="alert alert-success d-flex flex-wrap justify-content-between align-items-center gap-2" role="status">
            <span>{visible.length === 0 ? t('next_step.all_resolved') : t('next_step.some_remaining')}</span>
            <Link to={URLS.DEPOSIT_GL_CLASSIFICATION_STATUS} className="btn btn-sm btn-success">
              {t('next_step.go_to_deposits')}
            </Link>
          </div>
        )}

        {visible.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <p className="mb-0">{t('empty.no_results')}</p>
          </div>
        ) : visible.map((cluster) => {
          const evidence = cluster.transactions.find((t) => t.extracted_check_data)?.extracted_check_data;
          const isOpen = expanded === cluster.payee_key;

          return (
            <div key={cluster.payee_key} className="card mb-3 border shadow-sm">
              <div className="card-body">
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                  <div>
                    <span className="fw-semibold">{cluster.vendor_name}</span>
                    {evidence && <Badge bg="info" text="dark" className="ms-2">{t('badges.from_check')}</Badge>}
                    <div className="small text-muted">
                      {t('counts.transactions', { count: cluster.transaction_count })} · {money(cluster.total_amount)}
                      {cluster.earliest_date && ` · ${t('cluster.date_range', { from: cluster.earliest_date, to: cluster.latest_date })}`}
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2" style={{ minWidth: 340 }}>
                    <div style={{ minWidth: 220 }}>
                      <Select
                        options={vendors}
                        isClearable
                        placeholder={t('actions.assign_placeholder')}
                        value={vendors.find((v) => v.value === chosen[cluster.payee_key]) || null}
                        onChange={(option: any) =>
                          setChosen((c) => ({ ...c, [cluster.payee_key]: option ? option.value : null }))}
                      />
                    </div>
                    <Button
                      size="sm"
                      disabled={saving === cluster.payee_key || !chosen[cluster.payee_key]}
                      onClick={() => confirm(cluster)}
                    >
                      {saving === cluster.payee_key ? t('actions.assigning') : t('actions.confirm')}
                    </Button>
                    {evidence && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        disabled={saving === cluster.payee_key}
                        onClick={() => startCreateFromCheck(cluster)}
                      >
                        {t('actions.new_vendor')}
                      </Button>
                    )}
                  </div>
                </div>

                {checkAddresses(cluster).length > 1 && (
                  <div className="small mt-2" style={{ color: '#b45309' }}>
                    {t('cluster.multiple_addresses', { count: checkAddresses(cluster).length })}
                    <div className="d-flex flex-wrap gap-2 mt-1">
                      {checkAddresses(cluster).map((t) => (
                        <Button
                          key={t.transaction_id}
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => startCreateFromCheck(cluster, t.transaction_id)}
                        >
                          {(addressOf(t.extracted_check_data) || []).filter(Boolean).join(', ')}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {evidence && (
                  <div className="small text-muted mt-2">
                    {t('evidence.check_number', { number: evidence.check_number || '--' })}
                    {evidence.check_date && ` ${t('evidence.dated', { date: evidence.check_date })}`}
                    {evidence.memo && ` · ${t('evidence.memo', { memo: evidence.memo })}`}
                    {evidence.source_page && ` · ${t('evidence.page', { page: evidence.source_page })}`}
                    {evidence.source_file_id ? (
                      <Button variant="link" size="sm" className="p-0 ms-2 align-baseline"
                        disabled={opening === `head:${cluster.payee_key}`}
                        onClick={() => openEvidence(evidence, `head:${cluster.payee_key}`)}>
                        {opening === `head:${cluster.payee_key}` && <Spinner animation="border" size="sm" className="me-1" />}
                        {opening === `head:${cluster.payee_key}` ? t('viewer.opening') : t('actions.view_statement')}
                      </Button>
                    ) : (
                      <span className="ms-2">· {t('errors.source_unavailable')}</span>
                    )}
                  </div>
                )}

                <Button
                  variant="link"
                  size="sm"
                  className="p-0 mt-2"
                  onClick={() => setExpanded(isOpen ? null : cluster.payee_key)}
                >
                  {isOpen ? t('actions.hide_transactions') : t('actions.show_transactions', { count: cluster.transaction_count })}
                </Button>

                {isOpen && (
                  <div className="table-responsive mt-2">
                    <table className="table table-sm mb-0">
                      <thead className="table-light">
                        <tr><th>{t('table.headers.date')}</th><th>{t('table.headers.description')}</th><th className="text-end">{t('table.headers.amount')}</th><th /></tr>
                      </thead>
                      <tbody>
                        {cluster.transactions.map((tx) => {
                          const check = tx.extracted_check_data;
                          const key = `tx:${tx.transaction_id}`;
                          return (
                            <tr key={tx.transaction_id}>
                              <td className="small">{tx.posting_date || '--'}</td>
                              <td className="small">{tx.description || '--'}</td>
                              <td className="small text-end">{money(tx.amount)}</td>
                              <td className="small text-end">
                                {check?.source_file_id && (
                                  <Button variant="link" size="sm" className="p-0" disabled={opening === key} onClick={() => openEvidence(check, key)}>
                                    {opening === key && <Spinner animation="border" size="sm" className="me-1" />}
                                    {opening === key ? t('viewer.opening') : t('actions.view_check', { number: check.check_number || '--' })}
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {creatingFor && (
        <VendorFormModal
          show
          saving={creating}
          initial={formInitial}
          onClose={() => setCreatingFor(null)}
          onSubmit={createThenAssign}
        />
      )}
    </div>
  );
};

export default Tax1099PayeeReview;
